import React, { useState, useEffect, useCallback } from 'react';
import api from '../core/api';
import {
    type CampaignRow, type AdAccount, type MetaData,
    type ConversionEvent, type AlertsConfig,
    fmt, fmtUSD, fmtTime, statusBadge, accountStatusLabel,
} from './meta-ads/types';


// ─── Shared UI ────────────────────────────────────────────────────────────────

const MetricTile: React.FC<{ label: string; value: string; icon: string; color: string; sub?: string }> = ({ label, value, icon, color, sub }) => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 p-4">
        <span className={`material-symbols-outlined text-[20px] ${color}`}>{icon}</span>
        <p className="text-lg font-bold text-slate-800 dark:text-white mt-1">{value}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
        <p className="text-[11px] text-slate-400 mt-0.5">{label}</p>
    </div>
);

const PeriodSelector: React.FC<{ period: string; onChange: (p: 'today' | '7d' | '30d') => void }> = ({ period, onChange }) => (
    <div className="flex gap-1.5">
        {(['today', '7d', '30d'] as const).map(p => (
            <button key={p} onClick={() => onChange(p)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-colors ${period === p
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                {p === 'today' ? 'Hoy' : p === '7d' ? '7 días' : '30 días'}
            </button>
        ))}
    </div>
);

// ─── Tab: Campañas — Account List ──────────────────────────────────────────────

const AccountListView: React.FC<{ accounts: AdAccount[]; onSelect: (a: AdAccount) => void }> = ({ accounts, onSelect }) => {
    if (accounts.length === 0) return (
        <div className="flex flex-col items-center justify-center py-20 text-slate-300 gap-3">
            <span className="material-symbols-outlined text-[48px]">account_balance</span>
            <p className="text-sm">No hay cuentas publicitarias</p>
        </div>
    );
    return (
        <div className="space-y-3">
            {accounts.map(account => {
                const ins = account.insights;
                const st  = accountStatusLabel(account.status);
                const cpl = ins && ins.leads > 0 ? ins.spend / ins.leads : null;
                return (
                    <button key={account.id} onClick={() => onSelect(account)}
                        className="w-full text-left bg-white dark:bg-slate-card rounded-2xl border border-slate-200 dark:border-white/8 p-4 hover:border-blue-400 dark:hover:border-blue-500/50 hover:shadow-md transition-all group">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-black text-base shrink-0">f</div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{account.name}</p>
                                <p className="text-[11px] text-slate-400 font-mono">{account.id} · {account.currency}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                                <span className="material-symbols-outlined text-[18px] text-slate-300 group-hover:text-blue-400 transition-colors">chevron_right</span>
                            </div>
                        </div>
                        {ins ? (
                            <div className="grid grid-cols-4 gap-2">
                                {[
                                    { label: 'Gasto',      value: fmtUSD(ins.spend, account.currency), color: 'text-blue-600 dark:text-blue-400' },
                                    { label: 'Impresiones', value: fmt(ins.impressions, 0),            color: 'text-slate-700 dark:text-slate-200' },
                                    { label: 'Leads',      value: fmt(ins.leads, 0),                   color: 'text-brand-accent dark:text-brand-accent' },
                                    { label: 'Costo/lead', value: cpl ? fmtUSD(cpl, account.currency) : '—', color: 'text-slate-700 dark:text-slate-200' },
                                ].map(m => (
                                    <div key={m.label} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-2.5 text-center">
                                        <p className={`text-sm font-bold ${m.color}`}>{m.value}</p>
                                        <p className="text-[10px] text-slate-400">{m.label}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 text-center py-2">Sin datos en este período</p>
                        )}
                        <p className="text-[11px] text-slate-400 mt-2.5 flex items-center gap-1">
                            <span className="material-symbols-outlined text-[13px]">campaign</span>
                            {account.campaigns.length} campaña{account.campaigns.length !== 1 ? 's' : ''}
                        </p>
                    </button>
                );
            })}
        </div>
    );
};

// ─── Tab: Campañas — Account Detail ─────────────────────────────────────────────

const CampaignCard: React.FC<{ campaign: CampaignRow; currency: string; expanded: boolean; onToggle: () => void }> = ({ campaign: c, currency, expanded, onToggle }) => {
    const badge = statusBadge(c.status);
    const cpl   = c.leads && c.leads > 0 && c.spend ? c.spend / c.leads : null;
    return (
        <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/6 overflow-hidden">
            <button onClick={onToggle}
                className="w-full text-left px-4 py-3 flex items-center gap-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0 ${badge.cls}`}>{badge.label}</span>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex-1 truncate">{c.name}</p>
                {c.spend !== null && <p className="text-sm font-bold text-blue-600 dark:text-blue-400 shrink-0">{fmtUSD(c.spend, currency)}</p>}
                <span className={`material-symbols-outlined text-[16px] text-slate-300 transition-transform shrink-0 ${expanded ? 'rotate-180' : ''}`}>expand_more</span>
            </button>
            {expanded && (
                <div className="border-t border-slate-100 dark:border-white/5 px-4 py-3 bg-slate-50/60 dark:bg-slate-800/30 space-y-3">
                    <div className="flex flex-wrap gap-2 text-[11px]">
                        {c.objective && <span className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-white/6 px-2 py-0.5 rounded-lg text-slate-500">Objetivo: <b className="text-slate-600 dark:text-slate-300">{c.objective.replace(/_/g, ' ')}</b></span>}
                        {c.dailyBudget    && <span className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-white/6 px-2 py-0.5 rounded-lg text-slate-500">Diario: <b className="text-slate-600 dark:text-slate-300">{fmtUSD(c.dailyBudget, currency)}</b></span>}
                        {c.lifetimeBudget && <span className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-white/6 px-2 py-0.5 rounded-lg text-slate-500">Total: <b className="text-slate-600 dark:text-slate-300">{fmtUSD(c.lifetimeBudget, currency)}</b></span>}
                    </div>
                    {c.impressions !== null ? (
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { label: 'Impresiones', value: fmt(c.impressions, 0),          color: 'text-primary' },
                                { label: 'Alcance',     value: fmt(c.reach ?? 0, 0),            color: 'text-primary' },
                                { label: 'Clics',       value: fmt(c.clicks ?? 0, 0),           color: 'text-sky-500' },
                                { label: 'CTR',         value: `${(c.ctr ?? 0).toFixed(2)}%`,  color: 'text-teal-500' },
                                { label: 'CPC',         value: fmtUSD(c.cpc ?? 0, currency),   color: 'text-slate-500' },
                                { label: 'CPM',         value: fmtUSD(c.cpm ?? 0, currency),   color: 'text-slate-400' },
                                ...(c.leads != null   ? [{ label: 'Leads',      value: String(c.leads),                             color: 'text-brand-accent' }] : []),
                                ...(cpl               ? [{ label: 'Costo/lead', value: fmtUSD(cpl, currency),                      color: 'text-brand-accent' }] : []),
                                ...(c.purchases != null && c.purchases > 0 ? [{ label: 'Compras', value: String(c.purchases), color: 'text-primary' }] : []),
                            ].map(m => (
                                <div key={m.label} className="bg-white dark:bg-slate-800 rounded-xl p-2.5 text-center border border-slate-100 dark:border-white/5">
                                    <p className={`text-sm font-bold ${m.color}`}>{m.value}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">{m.label}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-slate-400 text-center py-1">Sin métricas en este período</p>
                    )}
                </div>
            )}
        </div>
    );
};

const AccountDetailView: React.FC<{ account: AdAccount; onBack: () => void }> = ({ account, onBack }) => {
    const ins = account.insights;
    const cpl = ins && ins.leads > 0 ? ins.spend / ins.leads : null;
    const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
    const active = account.campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE');
    const other  = account.campaigns.filter(c => c.status?.toUpperCase() !== 'ACTIVE');
    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <button onClick={onBack} className="w-8 h-8 rounded-xl flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-slate-800 dark:text-white truncate">{account.name}</p>
                    <p className="text-[11px] text-slate-400 font-mono">{account.id} · {account.currency}</p>
                </div>
            </div>
            {ins ? (
                <>
                    <div className="grid grid-cols-3 gap-3">
                        <MetricTile label="Gasto"       value={fmtUSD(ins.spend, account.currency)} icon="payments"   color="text-blue-500" />
                        <MetricTile label="Impresiones" value={fmt(ins.impressions, 0)}              icon="visibility" color="text-primary" />
                        <MetricTile label="Alcance"     value={fmt(ins.reach, 0)}                    icon="groups"     color="text-primary" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <MetricTile label="Clics"  value={fmt(ins.clicks, 0)}                  icon="ads_click" color="text-sky-500" />
                        <MetricTile label="CTR"    value={`${ins.ctr.toFixed(2)}%`}            icon="percent"   color="text-teal-500" />
                        <MetricTile label="CPC"    value={fmtUSD(ins.cpc, account.currency)}  icon="touch_app" color="text-slate-500" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <MetricTile label="CPM"       value={fmtUSD(ins.cpm, account.currency)} icon="bar_chart"  color="text-slate-400" />
                        <MetricTile label="Leads"     value={fmt(ins.leads, 0)}                  icon="person_add" color="text-brand-accent"
                            sub={cpl ? `${fmtUSD(cpl, account.currency)}/lead` : undefined} />
                        <MetricTile label="Frecuencia" value={ins.frequency.toFixed(2)}          icon="repeat"     color="text-orange-400" />
                    </div>
                </>
            ) : (
                <div className="rounded-2xl border border-slate-100 dark:border-white/6 bg-white dark:bg-slate-card p-6 text-center text-slate-400 text-sm">Sin métricas en este período</div>
            )}
            {account.campaigns.length > 0 ? (
                <div className="space-y-4">
                    {active.length > 0 && (
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-1">Activas ({active.length})</p>
                            <div className="space-y-2">{active.map(c => (
                                <CampaignCard key={c.id} campaign={c} currency={account.currency}
                                    expanded={expandedCampaign === c.id}
                                    onToggle={() => setExpandedCampaign(expandedCampaign === c.id ? null : c.id)} />
                            ))}</div>
                        </div>
                    )}
                    {other.length > 0 && (
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-1">Pausadas / Archivadas ({other.length})</p>
                            <div className="space-y-2">{other.map(c => (
                                <CampaignCard key={c.id} campaign={c} currency={account.currency}
                                    expanded={expandedCampaign === c.id}
                                    onToggle={() => setExpandedCampaign(expandedCampaign === c.id ? null : c.id)} />
                            ))}</div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="rounded-2xl border border-slate-100 dark:border-white/6 bg-white dark:bg-slate-card p-6 text-center text-slate-400 text-sm">No hay campañas en esta cuenta</div>
            )}
        </div>
    );
};

// ─── Tab: Conversiones ────────────────────────────────────────────────────────

const ConversionsTab: React.FC = () => {
    const [log, setLog]         = useState<ConversionEvent[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/monitoring/meta/conversion-log')
            .then(r => setLog(r.data.log || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const sent  = log.filter(e => e.status === 'sent').length;
    const error = log.filter(e => e.status === 'error').length;

    const eventIcon = (name: string) => name === 'Schedule' ? '📅' : name === 'Purchase' ? '💰' : '📋';

    return (
        <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-4 text-center">
                    <p className="text-xl font-bold text-slate-800 dark:text-white">{log.length}</p>
                    <p className="text-[11px] text-slate-400">Total eventos</p>
                </div>
                <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-4 text-center">
                    <p className="text-xl font-bold text-brand-accent">{sent}</p>
                    <p className="text-[11px] text-slate-400">Enviados</p>
                </div>
                <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-4 text-center">
                    <p className="text-xl font-bold text-brand-danger">{error}</p>
                    <p className="text-[11px] text-slate-400">Errores</p>
                </div>
            </div>

            {/* Refresh */}
            <div className="flex justify-end">
                <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                    <span className={`material-symbols-outlined text-[14px] ${loading ? 'animate-spin' : ''}`}>refresh</span>
                    Actualizar
                </button>
            </div>

            {/* Info box */}
            <div className="flex gap-2.5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                <span className="material-symbols-outlined text-blue-500 text-[18px] shrink-0 mt-0.5">info</span>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                    Cada vez que se agenda una cita (<b>Schedule</b>) o se completa una cita (<b>Purchase</b>), Diana envía el evento a Meta para optimizar el algoritmo de tus campañas.
                </p>
            </div>

            {/* Log */}
            {loading ? (
                <div className="flex items-center justify-center py-10 text-slate-300">
                    <span className="material-symbols-outlined animate-spin text-[32px]">progress_activity</span>
                </div>
            ) : log.length === 0 ? (
                <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-8 text-center text-slate-400 text-sm">
                    Aún no hay eventos enviados. Se generarán automáticamente cuando se agenden o completen citas.
                </div>
            ) : (
                <div className="space-y-2">
                    {log.map(e => (
                        <div key={e.id} className={`bg-white dark:bg-slate-card rounded-2xl border overflow-hidden ${e.status === 'error' ? 'border-brand-danger/20 dark:border-brand-danger/40' : 'border-slate-100 dark:border-white/8'}`}>
                            <div className="px-4 py-3 flex items-center gap-3">
                                <span className="text-lg shrink-0">{eventIcon(e.eventName)}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{e.eventName}</p>
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${e.status === 'sent' ? 'bg-brand-accent/12 text-brand-accent dark:bg-brand-accent/30 dark:text-brand-accent' : 'bg-brand-danger/12 text-brand-danger dark:bg-brand-danger/30 dark:text-brand-danger'}`}>
                                            {e.status === 'sent' ? 'Enviado' : 'Error'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-400 truncate">
                                        {e.patientName} · {e.patientPhoneMasked}
                                        {e.value != null && e.value > 0 ? ` · ${fmtUSD(e.value, e.currency)}` : ''}
                                    </p>
                                    {e.error && <p className="text-[11px] text-brand-danger mt-0.5 truncate">{e.error}</p>}
                                </div>
                                <p className="text-[11px] text-slate-400 shrink-0">{fmtTime(e.timestamp)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Tab: Alertas ────────────────────────────────────────────────────────────

const AlertsTab: React.FC = () => {
    const [config, setConfig]   = useState<AlertsConfig>({ enabled: false, cplThreshold: 0, silenceHours: 12, log: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [checking, setChecking] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/monitoring/meta/alerts-config')
            .then(r => setConfig(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post('/monitoring/meta/alerts-config', {
                enabled:      config.enabled,
                cplThreshold: config.cplThreshold,
                silenceHours: config.silenceHours,
            });
        } catch { /* ignore */ }
        finally { setSaving(false); }
    };

    const checkNow = async () => {
        setChecking(true);
        try {
            const r = await api.post('/monitoring/meta/alerts/check');
            alert(`Verificación completa: ${r.data.checked} campañas revisadas, ${r.data.alerts} alertas disparadas.`);
            load();
        } catch (e: any) {
            alert(e?.response?.data?.error || 'Error al verificar');
        } finally { setChecking(false); }
    };

    const alertTypeLabel = (type: string) =>
        type === 'no_leads_48h' ? '🚫 Sin leads 48h' : type === 'high_cpl' ? '💸 CPL alto' : '🔗 Sin captura en CRM';

    return (
        <div className="space-y-5">
            {loading ? (
                <div className="flex items-center justify-center py-10 text-slate-300">
                    <span className="material-symbols-outlined animate-spin text-[32px]">progress_activity</span>
                </div>
            ) : (
                <>
                    {/* Config card */}
                    <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-bold text-slate-800 dark:text-white">Alertas de campaña</p>
                                <p className="text-xs text-slate-400">Diana te avisa por WhatsApp si algo falla</p>
                            </div>
                            {/* Toggle */}
                            <button onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}
                                className={`relative w-11 h-6 rounded-full transition-colors ${config.enabled ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`}>
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${config.enabled ? 'translate-x-5' : ''}`} />
                            </button>
                        </div>

                        {config.enabled && (
                            <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-700">
                                <div>
                                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 block mb-1.5">
                                        Límite de CPL (costo por lead) en USD
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                                        <input type="number" min="0" step="1"
                                            value={config.cplThreshold || ''}
                                            onChange={e => setConfig(c => ({ ...c, cplThreshold: parseFloat(e.target.value) || 0 }))}
                                            placeholder="Ej: 50"
                                            className="w-full pl-7 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-1">Alerta si el costo por lead supera este valor. Pon 0 para desactivar.</p>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 block mb-1.5">
                                        Silencio entre alertas (horas)
                                    </label>
                                    <input type="number" min="1" max="72"
                                        value={config.silenceHours}
                                        onChange={e => setConfig(c => ({ ...c, silenceHours: parseInt(e.target.value) || 12 }))}
                                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
                                    <p className="text-[11px] text-slate-400 mt-1">No repite la alerta de la misma campaña hasta que pasen estas horas.</p>
                                </div>
                            </div>
                        )}

                        <div className="flex gap-2 pt-1">
                            <button onClick={save} disabled={saving}
                                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
                                {saving ? 'Guardando...' : 'Guardar configuración'}
                            </button>
                            <button onClick={checkNow} disabled={checking}
                                className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                                <span className={`material-symbols-outlined text-[16px] ${checking ? 'animate-spin' : ''}`}>search</span>
                                {checking ? 'Verificando...' : 'Verificar ahora'}
                            </button>
                        </div>
                    </div>

                    {/* What triggers alerts */}
                    <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-4 space-y-2">
                        <p className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide">Qué dispara una alerta</p>
                        <div className="space-y-2">
                            <div className="flex items-start gap-2.5">
                                <span className="text-base shrink-0">🚫</span>
                                <p className="text-xs text-slate-600 dark:text-slate-300"><b>Sin leads en 48h</b> — campaña activa con gasto real pero 0 leads. Posible problema en el formulario o segmentación.</p>
                            </div>
                            <div className="flex items-start gap-2.5">
                                <span className="text-base shrink-0">💸</span>
                                <p className="text-xs text-slate-600 dark:text-slate-300"><b>CPL alto</b> — costo por lead supera tu límite configurado. La notificación llega por WhatsApp al número del admin.</p>
                            </div>
                        </div>
                    </div>

                    {/* Alert log */}
                    <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2 px-1">
                            Alertas recientes ({config.log.length})
                        </p>
                        {config.log.length === 0 ? (
                            <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 p-6 text-center text-slate-400 text-sm">
                                Sin alertas registradas
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {config.log.map(a => (
                                    <div key={a.id} className="bg-white dark:bg-slate-card rounded-2xl border border-slate-100 dark:border-white/8 px-4 py-3 flex items-start gap-3">
                                        <span className="text-base shrink-0 mt-0.5">{a.type === 'no_leads_48h' ? '🚫' : a.type === 'high_cpl' ? '💸' : '🔗'}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">{a.campaignName}</p>
                                            <p className="text-xs text-slate-400 truncate">{a.accountName} · {alertTypeLabel(a.type)}</p>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{a.detail}</p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <p className="text-[11px] text-slate-400">{fmtTime(a.timestamp)}</p>
                                            {a.notified && (
                                                <span className="text-[10px] text-brand-accent flex items-center gap-0.5 justify-end mt-0.5">
                                                    <span className="material-symbols-outlined text-[11px]">check</span> WA enviado
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

type Tab = 'campaigns' | 'funnel' | 'conversions' | 'alerts' | 'optimize';

// ─── Tab: Optimizar ──────────────────────────────────────────────────────────

type InsightAction =
    | 'increase' | 'decrease' | 'pause' | 'scale'
    | 'bad_form' | 'creative_fatigue' | 'low_capture' | 'no_conversion';

interface BudgetRec {
    campaignId: string; campaignName: string;
    accountId: string; accountName: string; currency: string;
    currentBudget: number; suggestedBudget: number;
    action: InsightAction;
    reason: string; priority: 'high' | 'medium' | 'low';
    spend: number; leads: number; cpl: number | null; avgCpl: number;
    leadsDb?: number; appts?: number;
}

interface DianaReport {
    period7d:  { leads: number; appts: number; completed: number; convRate: number };
    period30d: { leads: number; appts: number; completed: number; convRate: number };
    allTime:   { leads: number };
    campaigns: { name: string; leads: number; appts: number; completed: number; convRate: number }[];
}

interface ActiveCampaignRow {
    id: string; name: string; accountName: string; currency: string;
    spend: number | null; leads: number | null; cpl: number | null;
    dailyBudget: number | null; impressions: number | null; ctr: number | null;
}

const OptimizeTab: React.FC = () => {
    const [campaigns, setCampaigns]         = useState<ActiveCampaignRow[]>([]);
    const [camLoading, setCamLoading]       = useState(true);
    const [camError, setCamError]           = useState<string | null>(null);
    const [report, setReport]               = useState<DianaReport | null>(null);
    const [reportLoading, setReportLoading] = useState(true);
    const [recs, setRecs]                   = useState<BudgetRec[]>([]);
    const [recsLoading, setRecsLoading]     = useState(false);
    const [applying, setApplying]           = useState<string | null>(null);
    const [applied, setApplied]             = useState<Set<string>>(new Set());
    const [recsError, setRecsError]         = useState<string | null>(null);
    const [reportSending, setReportSending] = useState(false);
    const [reportMsg, setReportMsg]         = useState<string | null>(null);

    useEffect(() => {
        api.get('/monitoring/meta-ads?period=7d')
            .then(r => {
                const rows: ActiveCampaignRow[] = [];
                for (const acc of r.data?.adAccounts || []) {
                    for (const c of acc.campaigns || []) {
                        if (c.status !== 'ACTIVE') continue;
                        const cpl = (c.leads && c.leads > 0 && c.spend) ? c.spend / c.leads : null;
                        rows.push({ id: c.id, name: c.name, accountName: acc.name, currency: acc.currency,
                            spend: c.spend, leads: c.leads, cpl, dailyBudget: c.dailyBudget,
                            impressions: c.impressions, ctr: c.ctr });
                    }
                }
                setCampaigns(rows.sort((a, b) => (b.spend || 0) - (a.spend || 0)));
            })
            .catch(e => setCamError(e?.response?.data?.error || 'Error al cargar campañas'))
            .finally(() => setCamLoading(false));

        api.get('/monitoring/meta/diana-report')
            .then(r => setReport(r.data))
            .catch(() => {})
            .finally(() => setReportLoading(false));
    }, []);

    const analyze = useCallback(async () => {
        setRecsLoading(true); setRecsError(null);
        try {
            const r = await api.get('/monitoring/meta/budget-insights');
            setRecs(r.data.recommendations || []);
        } catch (e: any) {
            setRecsError(e?.response?.data?.error || 'Error al analizar campañas');
        } finally { setRecsLoading(false); }
    }, []);

    const apply = async (rec: BudgetRec) => {
        setApplying(rec.campaignId);
        try {
            await api.post('/monitoring/meta/budget-apply', { campaignId: rec.campaignId, newDailyBudget: rec.suggestedBudget, action: rec.action });
            setApplied(prev => new Set([...prev, rec.campaignId]));
        } catch (e: any) {
            alert(e?.response?.data?.error || 'Error al aplicar cambio');
        } finally { setApplying(null); }
    };

    const sendReport = async () => {
        setReportSending(true); setReportMsg(null);
        try {
            const r = await api.post('/monitoring/meta/weekly-report');
            setReportMsg(r.data.sent ? '✅ Reporte enviado por WhatsApp' : '⚠️ ' + r.data.message);
        } catch (e: any) {
            setReportMsg('❌ ' + (e?.response?.data?.error || 'Error'));
        } finally { setReportSending(false); }
    };

    const actionConfig: Record<InsightAction, {
        icon: string; color: string; bg: string; btnCls: string;
        label: string; applyLabel?: string; infoOnly?: boolean;
    }> = {
        // ── Presupuesto ───────────────────────────────────────────────────────
        increase:         { icon: 'trending_up',      color: 'text-brand-accent', bg: 'bg-brand-accent/8 dark:bg-brand-accent/20 border-brand-accent/20 dark:border-brand-accent/30',   btnCls: 'bg-brand-accent hover:bg-brand-accent text-white',     label: 'Aumentar presupuesto' },
        scale:            { icon: 'rocket_launch',    color: 'text-brand-accent', bg: 'bg-brand-accent/8 dark:bg-brand-accent/20 border-brand-accent/30 dark:border-brand-accent/40',   btnCls: 'bg-brand-accent hover:bg-brand-accent text-white',     label: 'Escalar campaña ganadora' },
        decrease:         { icon: 'trending_down',    color: 'text-amber-500',   bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/30',           btnCls: 'bg-amber-500 hover:bg-amber-600 text-white',         label: 'Reducir presupuesto' },
        pause:            { icon: 'pause_circle',     color: 'text-brand-danger',    bg: 'bg-brand-danger/8 dark:bg-brand-danger/20 border-brand-danger/20 dark:border-brand-danger/30',               btnCls: 'bg-brand-danger hover:bg-brand-danger text-white',           label: 'Pausar campaña' },
        // ── Creativos / Formulario ───────────────────────────────────────────
        bad_form:         { icon: 'edit_note',        color: 'text-amber-500',   bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/30',           btnCls: 'bg-slate-600 hover:bg-slate-700 text-white',         label: 'Formulario con fricción', infoOnly: true },
        creative_fatigue: { icon: 'image_not_supported', color: 'text-primary', bg: 'bg-primary/8 dark:bg-primary/20 border-primary/20 dark:border-primary/30',    btnCls: 'bg-slate-600 hover:bg-slate-700 text-white',         label: 'Creativos agotados', infoOnly: true },
        // ── CRM / Seguimiento ─────────────────────────────────────────────────
        low_capture:      { icon: 'cloud_off',        color: 'text-brand-danger',    bg: 'bg-brand-danger/8 dark:bg-brand-danger/20 border-brand-danger/20 dark:border-brand-danger/30',               btnCls: 'bg-slate-600 hover:bg-slate-700 text-white',         label: 'Leads sin llegar al CRM', infoOnly: true },
        no_conversion:    { icon: 'person_off',       color: 'text-amber-500',   bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/30',           btnCls: 'bg-slate-600 hover:bg-slate-700 text-white',         label: 'Sin seguimiento en CRM', infoOnly: true },
    };
    const priorityDot = { high: 'bg-brand-danger/90', medium: 'bg-amber-400', low: 'bg-slate-300' };
    const isBudgetAction = (a: InsightAction) => ['increase', 'decrease', 'pause', 'scale'].includes(a);

    return (
        <div className="space-y-6">

            {/* ── Campañas activas ── */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Campañas activas · 7 días</h2>
                    {campaigns.length > 0 && (
                        <span className="text-[10px] font-semibold bg-brand-accent/12 dark:bg-brand-accent/30 text-brand-accent dark:text-brand-accent px-2 py-0.5 rounded-full">
                            {campaigns.length} activa{campaigns.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
                {camLoading && (
                    <div className="flex justify-center py-8 text-slate-300">
                        <span className="material-symbols-outlined animate-spin text-[28px]">progress_activity</span>
                    </div>
                )}
                {!camLoading && camError && (
                    <div className="rounded-2xl border border-brand-danger/20 dark:border-brand-danger/40 bg-brand-danger/8 dark:bg-brand-danger/20 p-4 text-xs text-brand-danger">{camError}</div>
                )}
                {!camLoading && !camError && campaigns.length === 0 && (
                    <div className="rounded-2xl border border-slate-100 dark:border-white/6 bg-white dark:bg-slate-800 p-6 text-center">
                        <span className="material-symbols-outlined text-slate-200 dark:text-slate-700 text-[36px]">campaign</span>
                        <p className="text-xs text-slate-400 mt-2">Sin campañas activas en los últimos 7 días</p>
                    </div>
                )}
                {campaigns.map(c => {
                    const dianaData = report?.campaigns?.find(
                        rc => rc.name.toLowerCase() === c.name.toLowerCase()
                    );
                    return (
                    <div key={c.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{c.name}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">{c.accountName}</p>
                            </div>
                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 shrink-0">
                                {c.spend !== null ? fmtUSD(c.spend, c.currency) : '—'}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-2 text-center text-xs">
                            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-2">
                                <p className="font-bold text-slate-800 dark:text-white">{c.impressions !== null ? fmt(c.impressions, 0) : '—'}</p>
                                <p className="text-[10px] text-slate-400">Imp.</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-2">
                                <p className="font-bold text-slate-800 dark:text-white">{c.ctr !== null ? `${c.ctr.toFixed(2)}%` : '—'}</p>
                                <p className="text-[10px] text-slate-400">CTR</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-2">
                                <p className="font-bold text-brand-accent dark:text-brand-accent">{c.leads !== null ? c.leads : '0'}</p>
                                <p className="text-[10px] text-slate-400">Leads Meta</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-2">
                                <p className="font-bold text-slate-800 dark:text-white">{c.cpl !== null ? fmtUSD(c.cpl, c.currency) : '—'}</p>
                                <p className="text-[10px] text-slate-400">CPL</p>
                            </div>
                        </div>

                        {/* Cruce de datos Diana */}
                        {dianaData && (
                            <div className="pt-2.5 border-t border-slate-100 dark:border-white/5 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                                <div className="flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[13px] text-slate-400">sync</span>
                                    <span>Resultados CRM:</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span>Leads: <b className="text-slate-700 dark:text-slate-200">{dianaData.leads}</b></span>
                                    <span>Citas: <b className="text-slate-700 dark:text-slate-200">{dianaData.appts}</b></span>
                                    <span>Completadas: <b className="text-slate-700 dark:text-slate-200">{dianaData.completed}</b></span>
                                    <span className={`font-semibold ${convColor(dianaData.convRate)}`}>{dianaData.convRate}% conv.</span>
                                </div>
                            </div>
                        )}
                    </div>
                )})}
            </section>

            {/* ── Recomendaciones ── */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Optimización de Presupuesto</h2>
                    <button onClick={analyze} disabled={recsLoading}
                        className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                        <span className={`material-symbols-outlined text-[14px] ${recsLoading ? 'animate-spin' : ''}`}>online_prediction</span>
                        Analizar campañas
                    </button>
                </div>

                {recsLoading && (
                    <div className="flex flex-col items-center justify-center py-10 text-slate-400 text-xs gap-3">
                        <div className="w-6 h-6 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
                        Analizando métricas con Diana IA...
                    </div>
                )}

                {!recsLoading && recsError && (
                    <div className="rounded-2xl border border-brand-danger/20 dark:border-brand-danger/40 bg-brand-danger/8 dark:bg-brand-danger/20 p-4 text-xs text-brand-danger">{recsError}</div>
                )}

                {!recsLoading && !recsError && recs.length === 0 && (
                    <div className="rounded-2xl border border-slate-100 dark:border-white/6 bg-slate-50 dark:bg-slate-800/60 p-5 text-center space-y-1">
                        <span className="material-symbols-outlined text-slate-200 dark:text-slate-700 text-[32px]">tune</span>
                        <p className="text-xs text-slate-400">Diana analiza 7 tipos de señales: presupuesto, formulario, creativos agotados, captura al CRM y seguimiento.</p>
                    </div>
                )}

                {recs.map((rec, idx) => {
                    const cfg  = actionConfig[rec.action];
                    const done = applied.has(rec.campaignId + rec.action);
                    const busy = applying === rec.campaignId;
                    const isInfo = cfg.infoOnly;
                    return (
                        <div key={`${rec.campaignId}-${idx}`} className={`rounded-2xl border p-4 space-y-3 ${done ? 'opacity-50' : ''} ${cfg.bg}`}>
                            {/* Header */}
                            <div className="flex items-start gap-2.5">
                                <span className={`material-symbols-outlined text-[22px] ${cfg.color} shrink-0`}>{cfg.icon}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{rec.campaignName}</p>
                                        <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${priorityDot[rec.priority] === 'bg-brand-danger/90' ? 'bg-brand-danger/12 text-brand-danger' : priorityDot[rec.priority] === 'bg-amber-400' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'}`}>
                                            {rec.priority === 'high' ? 'Urgente' : rec.priority === 'medium' ? 'Recomendado' : 'Opcional'}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{rec.accountName} · <span className="font-medium">{cfg.label}</span></p>
                                </div>
                                {done && <span className="material-symbols-outlined text-brand-accent text-[20px] shrink-0">check_circle</span>}
                            </div>
                            {/* Reason */}
                            <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{rec.reason}</p>
                            {/* Stats row */}
                            <div className="flex flex-wrap gap-1.5 text-[11px]">
                                <span className="bg-white/70 dark:bg-black/20 px-2 py-0.5 rounded-lg text-slate-600 dark:text-slate-300">
                                    Gasto 7d: <b>${rec.spend.toFixed(2)}</b>
                                </span>
                                <span className="bg-white/70 dark:bg-black/20 px-2 py-0.5 rounded-lg text-slate-600 dark:text-slate-300">
                                    Leads Meta: <b>{rec.leads}</b>
                                </span>
                                {rec.cpl != null && (
                                    <span className="bg-white/70 dark:bg-black/20 px-2 py-0.5 rounded-lg text-slate-600 dark:text-slate-300">
                                        CPL: <b>${rec.cpl.toFixed(2)}</b>
                                    </span>
                                )}
                                {rec.leadsDb != null && rec.leadsDb >= 0 && (
                                    <span className="bg-white/70 dark:bg-black/20 px-2 py-0.5 rounded-lg text-slate-600 dark:text-slate-300">
                                        En CRM: <b>{rec.leadsDb}</b>
                                    </span>
                                )}
                                {rec.appts != null && rec.appts >= 0 && (
                                    <span className="bg-white/70 dark:bg-black/20 px-2 py-0.5 rounded-lg text-slate-600 dark:text-slate-300">
                                        Citas: <b>{rec.appts}</b>
                                    </span>
                                )}
                            </div>
                            {/* Budget change row — solo para acciones de presupuesto */}
                            {isBudgetAction(rec.action) && rec.action !== 'pause' && rec.currentBudget > 0 && rec.suggestedBudget > 0 && (
                                <div className="flex items-center gap-2 text-xs bg-white/50 dark:bg-black/20 rounded-xl px-3 py-2">
                                    <span className="text-slate-500">Presupuesto/día:</span>
                                    <span className="font-semibold text-slate-700 dark:text-slate-200">${rec.currentBudget}</span>
                                    <span className="material-symbols-outlined text-[14px] text-slate-400">arrow_forward</span>
                                    <span className="font-bold text-slate-800 dark:text-white">${rec.suggestedBudget}</span>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-auto ${rec.action === 'increase' || rec.action === 'scale' ? 'bg-brand-accent/12 text-brand-accent' : 'bg-amber-100 text-amber-700'}`}>
                                        {rec.suggestedBudget > rec.currentBudget ? '+' : ''}{Math.round((rec.suggestedBudget / rec.currentBudget - 1) * 100)}%
                                    </span>
                                </div>
                            )}
                            {/* CTA */}
                            {!done && !isInfo && (
                                <button onClick={() => { apply(rec).then(() => setApplied(prev => new Set([...prev, rec.campaignId + rec.action]))); }} disabled={busy}
                                    className={`w-full py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${cfg.btnCls}`}>
                                    {busy ? 'Aplicando...' :
                                        rec.action === 'pause'    ? 'Pausar campaña' :
                                        rec.action === 'scale'    ? `Escalar a $${rec.suggestedBudget}/día` :
                                        rec.action === 'increase' ? `Aumentar a $${rec.suggestedBudget}/día` :
                                                                    `Reducir a $${rec.suggestedBudget}/día`}
                                </button>
                            )}
                            {!done && isInfo && (
                                <button onClick={() => setApplied(prev => new Set([...prev, rec.campaignId + rec.action]))}
                                    className="w-full py-2 rounded-xl text-xs font-semibold bg-white/60 dark:bg-black/20 text-slate-600 dark:text-slate-300 hover:bg-white/80 transition-colors border border-white/40">
                                    Entendido — marcar como revisado
                                </button>
                            )}
                        </div>
                    );
                })}
            </section>

            {/* ── Reporte Semanal ── */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 p-5 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600">
                        <span className="material-symbols-outlined">summarize</span>
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-800 dark:text-white">Reporte Semanal de Rendimiento</h2>
                        <p className="text-xs text-slate-400">Diana envía un resumen cada lunes a las 8:00 AM</p>
                    </div>
                </div>

                {reportLoading ? (
                    <p className="text-xs text-slate-400 text-center py-4">Cargando reporte de rendimiento...</p>
                ) : report ? (
                    <div className="grid grid-cols-3 gap-2.5 text-center text-xs bg-slate-50 dark:bg-slate-900 rounded-xl p-3 border border-slate-100 dark:border-white/5">
                        <div>
                            <p className="font-bold text-slate-800 dark:text-white">{report.period30d.leads}</p>
                            <p className="text-[10px] text-slate-400">Leads CRM (30d)</p>
                        </div>
                        <div>
                            <p className="font-bold text-slate-800 dark:text-white">{report.period30d.appts}</p>
                            <p className="text-[10px] text-slate-400">Citas agendadas</p>
                        </div>
                        <div>
                            <p className="font-bold text-brand-accent">{report.period30d.convRate}%</p>
                            <p className="text-[10px] text-slate-400">Tasa de conversión</p>
                        </div>
                    </div>
                ) : null}

                <div className="flex gap-2">
                    <button onClick={sendReport} disabled={reportSending}
                        className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
                        <span className="material-symbols-outlined text-[15px]">send_to_mobile</span>
                        {reportSending ? 'Enviando...' : 'Enviar reporte actual por WhatsApp'}
                    </button>
                </div>
                {reportMsg && (
                    <p className="text-xs text-center font-medium text-slate-500 mt-1">{reportMsg}</p>
                )}
            </section>
        </div>
    );
};

// ─── Tab: Funnel unificado ───────────────────────────────────────────────────

interface FunnelCampaign {
    id: string; name: string; accountId: string; accountName: string; status: string;
    impressions: number; clicks: number; leadsAds: number; spend: number; ctr: number;
    leadsDb: number; appts: number; completed: number;
    cpl: number | null; cpa: number | null; leadConv: number; apptConv: number;
}
interface UnifiedFunnel {
    period: string;
    funnel: { stage: string; value: number; conv: number | null; icon: string }[];
    totals: {
        spend: number; impressions: number; clicks: number;
        leadsAds: number; leadsDb: number; appts: number; completed: number;
        cpl: number | null; cpa: number | null;
    };
    campaigns: FunnelCampaign[];
}

interface AccountSummary {
    id: string; name: string;
    spend: number; impressions: number; clicks: number;
    leadsAds: number; leadsDb: number; appts: number; completed: number;
    cpl: number | null; cpa: number | null; ctr: number;
}

function buildAccountSummary(campaigns: FunnelCampaign[]): AccountSummary[] {
    const map = new Map<string, AccountSummary>();
    for (const c of campaigns) {
        if (!map.has(c.accountId)) {
            map.set(c.accountId, {
                id: c.accountId, name: c.accountName,
                spend: 0, impressions: 0, clicks: 0,
                leadsAds: 0, leadsDb: 0, appts: 0, completed: 0,
                cpl: null, cpa: null, ctr: 0,
            });
        }
        const a = map.get(c.accountId)!;
        a.spend       += c.spend;
        a.impressions += c.impressions;
        a.clicks      += c.clicks;
        a.leadsAds    += c.leadsAds;
        a.leadsDb     += c.leadsDb;
        a.appts       += c.appts;
        a.completed   += c.completed;
    }
    for (const a of map.values()) {
        a.ctr = a.impressions > 0 ? Math.round(a.clicks / a.impressions * 1000) / 10 : 0;
        a.cpl = a.leadsAds > 0 ? Math.round(a.spend / a.leadsAds * 100) / 100 : null;
        a.cpa = a.completed > 0 ? Math.round(a.spend / a.completed * 100) / 100 : null;
    }
    return [...map.values()];
}

const convColor = (pct: number) =>
    pct >= 40 ? 'text-brand-accent dark:text-brand-accent'
    : pct >= 15 ? 'text-amber-600 dark:text-amber-400'
    : 'text-brand-danger dark:text-brand-danger';

const convBadge = (pct: number) =>
    pct >= 40 ? 'bg-brand-accent/12 text-brand-accent dark:bg-brand-accent/30 dark:text-brand-accent'
    : pct >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    : 'bg-brand-danger/12 text-brand-danger dark:bg-brand-danger/30 dark:text-brand-danger';

const FunnelTab: React.FC = () => {
    const [data, setData]             = useState<UnifiedFunnel | null>(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState<string | null>(null);
    const [period, setPeriod]         = useState<'7d' | '30d'>('30d');
    const [selectedAcct, setSelectedAcct] = useState<string>('all');
    const [expanded, setExpanded]     = useState<string | null>(null);

    const load = useCallback((p: string) => {
        setLoading(true); setError(null);
        api.get(`/monitoring/meta/unified-funnel?period=${p}`)
            .then(r => setData(r.data))
            .catch(e => setError(e?.response?.data?.error || 'Error al cargar funnel'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(period); }, [period]);

    if (loading) return (
        <div className="flex justify-center py-16 text-slate-300">
            <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
        </div>
    );
    if (error) return (
        <div className="rounded-2xl border border-brand-danger/20 dark:border-brand-danger/40 bg-brand-danger/8 dark:bg-brand-danger/20 p-6 text-center space-y-2">
            <span className="material-symbols-outlined text-brand-danger text-[36px]">error</span>
            <p className="text-sm text-brand-danger dark:text-brand-danger">{error}</p>
        </div>
    );
    if (!data) return null;

    const { totals } = data;
    const accounts   = buildAccountSummary(data.campaigns);
    const allCampaigns = selectedAcct === 'all'
        ? data.campaigns
        : data.campaigns.filter(c => c.accountId === selectedAcct);

    const T = selectedAcct === 'all' ? totals : (() => {
        const sel = accounts.find(a => a.id === selectedAcct);
        return sel ? {
            spend: sel.spend, impressions: sel.impressions, clicks: sel.clicks,
            leadsAds: sel.leadsAds, leadsDb: sel.leadsDb, appts: sel.appts,
            completed: sel.completed, cpl: sel.cpl, cpa: sel.cpa,
        } : totals;
    })();

    const ctr      = T.impressions > 0 ? Math.round(T.clicks      / T.impressions * 1000) / 10 : 0;
    const formRate = T.clicks      > 0 ? Math.round(T.leadsAds    / T.clicks      * 1000) / 10 : 0;
    const capture  = T.leadsAds    > 0 ? Math.round(T.leadsDb     / T.leadsAds    * 1000) / 10 : 0;
    const apptRate = T.leadsDb     > 0 ? Math.round(T.appts       / T.leadsDb     * 1000) / 10 : 0;
    const doneRate = T.appts       > 0 ? Math.round(T.completed   / T.appts       * 1000) / 10 : 0;

    const fmtN = (n: number) =>
        n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
        : n >= 1_000   ? `${(n / 1_000).toFixed(1)}K`
        : n.toLocaleString();

    const funnelSteps = [
        { icon: 'visibility',      label: 'Impresiones',         value: T.impressions, conv: null,     desc: null },
        { icon: 'ads_click',       label: 'Clics al anuncio',    value: T.clicks,      conv: ctr,      desc: `${ctr}% CTR — de cada 100 personas que vieron el anuncio, ${ctr} hicieron clic` },
        { icon: 'contact_page',    label: 'Leads en Meta Ads',   value: T.leadsAds,    conv: formRate, desc: `${formRate}% completaron el formulario — de los que hicieron clic` },
        { icon: 'person_add',      label: 'Pacientes en CRM',    value: T.leadsDb,     conv: capture,  desc: `${capture}% llegaron al CRM — webhook capturó este % de los leads de Meta` },
        { icon: 'event_available', label: 'Citas agendadas',     value: T.appts,       conv: apptRate, desc: `${apptRate}% agendaron cita — de los pacientes registrados en CRM` },
        { icon: 'task_alt',        label: 'Citas completadas',   value: T.completed,   conv: doneRate, desc: `${doneRate}% completaron su cita — de las citas agendadas` },
    ];

    return (
        <div className="space-y-4">
            {/* Header: period + account selector */}
            <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Período</p>
                    <div className="flex gap-1.5">
                        {(['7d', '30d'] as const).map(p => (
                            <button key={p} onClick={() => setPeriod(p)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${period === p
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                                {p === '7d' ? '7 días' : '30 días'}
                            </button>
                        ))}
                    </div>
                </div>
                {accounts.length > 1 && (
                    <div>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5">Cuenta publicitaria</p>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                onClick={() => setSelectedAcct('all')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${selectedAcct === 'all'
                                    ? 'bg-slate-800 dark:bg-white text-white dark:text-slate-900'
                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                                Todas ({accounts.length})
                            </button>
                            {accounts.map(a => (
                                <button key={a.id} onClick={() => setSelectedAcct(a.id)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${selectedAcct === a.id
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                                    {a.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 gap-2">
                {[
                    { label: 'Inversión total',   value: T.spend     > 0    ? fmtUSD(T.spend)   : '—', sub: `${fmtN(T.impressions)} impresiones`, icon: 'payments',      color: 'text-blue-500' },
                    { label: 'Costo por lead',    value: T.cpl      != null  ? fmtUSD(T.cpl)    : '—', sub: `${T.leadsAds} leads captados`,       icon: 'contact_page',  color: 'text-primary' },
                    { label: 'Tasa Meta → CRM',   value: T.leadsAds  > 0    ? `${capture}%`     : '—', sub: `${T.leadsDb} de ${T.leadsAds} leads`, icon: 'person_add',    color: 'text-primary' },
                    { label: 'Costo por cita ✓',  value: T.cpa      != null  ? fmtUSD(T.cpa)    : '—', sub: `${T.completed} citas completadas`,    icon: 'task_alt',      color: 'text-brand-accent' },
                ].map(k => (
                    <div key={k.label} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 p-3">
                        <span className={`material-symbols-outlined text-[18px] ${k.color}`}>{k.icon}</span>
                        <p className="text-base font-bold text-slate-800 dark:text-white mt-1">{k.value}</p>
                        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{k.label}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{k.sub}</p>
                    </div>
                ))}
            </div>

            {/* Funnel step-by-step */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 p-4 space-y-0">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                    Recorrido del lead {selectedAcct !== 'all' && `— ${accounts.find(a => a.id === selectedAcct)?.name}`}
                </p>
                {funnelSteps.map((step, i) => (
                    <div key={step.label}>
                        {/* Stage row */}
                        <div className="flex items-center gap-3 py-2.5">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                                i === 0 ? 'bg-blue-100 dark:bg-blue-900/30' :
                                i === 1 ? 'bg-primary/12 dark:bg-primary/30' :
                                i === 2 ? 'bg-primary/12 dark:bg-primary/30' :
                                i === 3 ? 'bg-primary/12 dark:bg-primary/30' :
                                i === 4 ? 'bg-fuchsia-100 dark:bg-fuchsia-900/30' :
                                          'bg-brand-accent/12 dark:bg-brand-accent/30'}`}>
                                <span className={`material-symbols-outlined text-[16px] ${
                                    i === 0 ? 'text-blue-500' :
                                    i === 1 ? 'text-primary' :
                                    i === 2 ? 'text-primary' :
                                    i === 3 ? 'text-primary' :
                                    i === 4 ? 'text-fuchsia-500' :
                                              'text-brand-accent'}`}>{step.icon}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{step.label}</p>
                                {step.desc && (
                                    <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">{step.desc}</p>
                                )}
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-base font-bold text-slate-800 dark:text-white">{fmtN(step.value)}</p>
                            </div>
                        </div>
                        {/* Conversion arrow between stages */}
                        {i < funnelSteps.length - 1 && funnelSteps[i + 1].conv !== null && (
                            <div className="flex items-center gap-2 pl-10 pb-1">
                                <div className="w-0.5 h-4 bg-slate-100 dark:bg-slate-700 ml-3.5"></div>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${convBadge(funnelSteps[i + 1].conv!)}`}>
                                    {funnelSteps[i + 1].conv}% pasan a la siguiente etapa
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Campaign breakdown */}
            {allCampaigns.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1">
                        Campañas ({allCampaigns.length})
                    </p>
                    {allCampaigns.map(c => {
                        const isOpen = expanded === c.id;
                        const cCtr     = c.impressions > 0 ? Math.round(c.clicks / c.impressions * 1000) / 10 : 0;
                        const cForm    = c.clicks > 0 ? Math.round(c.leadsAds / c.clicks * 1000) / 10 : 0;
                        const cCapture = c.leadsAds > 0 ? Math.round(c.leadsDb / c.leadsAds * 1000) / 10 : 0;
                        const cAppt    = c.leadsDb > 0 ? Math.round(c.appts / c.leadsDb * 1000) / 10 : 0;
                        return (
                            <div key={c.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 overflow-hidden">
                                <button className="w-full p-4 text-left" onClick={() => setExpanded(isOpen ? null : c.id)}>
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{c.name}</p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">{c.accountName}</p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                                c.status === 'ACTIVE' ? 'bg-brand-accent/12 text-brand-accent'
                                                : 'bg-slate-100 text-slate-500'}`}>
                                                {c.status === 'ACTIVE' ? 'Activa' : 'Pausada'}
                                            </span>
                                            <span className={`material-symbols-outlined text-[16px] text-slate-300 transition-transform ${isOpen ? 'rotate-90' : ''}`}>
                                                chevron_right
                                            </span>
                                        </div>
                                    </div>
                                    {/* Spend + mini funnel */}
                                    <div className="flex items-center gap-3 text-[11px]">
                                        {c.spend > 0 && (
                                            <span className="font-bold text-blue-600 dark:text-blue-400">{fmtUSD(c.spend)}</span>
                                        )}
                                        <span className="text-slate-300 dark:text-slate-600">·</span>
                                        <span className="text-slate-500">{fmtN(c.impressions)} imp.</span>
                                        <span className="text-slate-300 dark:text-slate-600">·</span>
                                        <span className={convColor(cCtr)}>{cCtr}% CTR</span>
                                        <span className="text-slate-300 dark:text-slate-600 ml-auto">·</span>
                                        {[
                                            { v: c.leadsAds,  label: 'leads' },
                                            { v: c.leadsDb,   label: 'CRM' },
                                            { v: c.appts,     label: 'citas' },
                                            { v: c.completed, label: '✓' },
                                        ].map((s, idx) => (
                                            <React.Fragment key={s.label}>
                                                {idx > 0 && <span className="text-slate-300 dark:text-slate-600">→</span>}
                                                <span className={`font-semibold ${s.v > 0 ? 'text-slate-700 dark:text-slate-200' : 'text-slate-300'}`}>
                                                    {s.v} <span className="font-normal text-slate-400">{s.label}</span>
                                                </span>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="border-t border-slate-100 dark:border-white/6 p-4 space-y-3">
                                        {/* Conversion breakdown */}
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { label: 'CTR (clic/impresión)', value: `${cCtr}%`, good: cCtr >= 2, desc: '% de impresiones que generaron un clic' },
                                                { label: 'Tasa de formulario',   value: `${cForm}%`, good: cForm >= 5, desc: '% de clics que completaron el formulario' },
                                                { label: 'Captura CRM',          value: `${cCapture}%`, good: cCapture >= 70, desc: '% de leads de Meta que llegaron al CRM' },
                                                { label: 'Cita agendada',        value: `${cAppt}%`, good: cAppt >= 30, desc: '% de pacientes CRM que agendaron cita' },
                                            ].map(m => (
                                                <div key={m.label} className={`rounded-xl p-2.5 ${m.good ? 'bg-brand-accent/8 dark:bg-brand-accent/20' : 'bg-brand-danger/8 dark:bg-brand-danger/20'}`}>
                                                    <p className={`text-base font-bold ${m.good ? 'text-brand-accent dark:text-brand-accent' : 'text-brand-danger dark:text-brand-danger'}`}>{m.value}</p>
                                                    <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">{m.label}</p>
                                                    <p className="text-[9px] text-slate-400 mt-0.5">{m.desc}</p>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Raw numbers */}
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs pt-1">
                                            {[
                                                ['Inversión',          c.spend > 0 ? fmtUSD(c.spend) : '—'],
                                                ['Impresiones',         c.impressions > 0 ? fmtN(c.impressions) : '—'],
                                                ['Clics',               c.clicks > 0 ? fmtN(c.clicks) : '—'],
                                                ['Leads Meta Ads',      c.leadsAds.toString()],
                                                ['Pacientes en CRM',    c.leadsDb.toString()],
                                                ['Citas agendadas',     c.appts.toString()],
                                                ['Citas completadas',   c.completed.toString()],
                                                ['CPL',                 c.cpl != null ? fmtUSD(c.cpl) : '—'],
                                                ['Costo por cita ✓',   c.cpa != null ? fmtUSD(c.cpa) : '—'],
                                            ].map(([lbl, val]) => (
                                                <div key={lbl} className="flex items-baseline justify-between gap-1">
                                                    <span className="text-slate-400 truncate">{lbl}</span>
                                                    <span className="font-semibold text-slate-700 dark:text-slate-200 shrink-0">{val}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {allCampaigns.length === 0 && !loading && (
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-white/6 p-6 text-center">
                    <span className="material-symbols-outlined text-slate-200 dark:text-slate-700 text-[40px]">funnel</span>
                    <p className="text-sm text-slate-500 mt-2">Sin datos de campaña para el período</p>
                    <p className="text-xs text-slate-400 mt-1">Conecta Meta Ads y activa campañas para ver el funnel</p>
                </div>
            )}
        </div>
    );
};

const MetaAds: React.FC = () => {
    const [data, setData]       = useState<MetaData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);
    const [period, setPeriod]   = useState<'today' | '7d' | '30d'>('30d');
    const [selected, setSelected] = useState<AdAccount | null>(null);
    const [tab, setTab]         = useState<Tab>('campaigns');

    const loadCampaigns = (p: string) => {
        setLoading(true);
        setError(null);
        api.get(`/monitoring/meta-ads?period=${p}`)
            .then(r => { setData(r.data); setSelected(null); })
            .catch(e => setError(e?.response?.data?.error || 'Error al cargar datos de Meta'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadCampaigns(period); }, [period]);

    const tabs: { id: Tab; label: string; icon: string }[] = [
        { id: 'campaigns',   label: 'Campañas',   icon: 'campaign' },
        { id: 'funnel',      label: 'Funnel',     icon: 'filter_alt' },
        { id: 'optimize',    label: 'Optimizar',  icon: 'tune' },
        { id: 'conversions', label: 'Conversión', icon: 'sync_alt' },
        { id: 'alerts',      label: 'Alertas',    icon: 'notifications_active' },
    ];

    return (
        <div className="px-4 pt-6 pb-10 max-w-3xl mx-auto space-y-5">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-[14px] font-black">f</span>
                        Meta Ads
                    </h1>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {tab === 'campaigns' && (selected ? selected.name : 'Cuentas publicitarias')}
                        {tab === 'conversions' && 'Loop de conversión'}
                        {tab === 'funnel'      && 'Funnel unificado Meta → CRM'}
                        {tab === 'alerts'      && 'Alertas de rendimiento'}
                        {tab === 'optimize'    && 'Optimización de presupuesto'}
                    </p>
                </div>
                {tab === 'campaigns' && (
                    <div className="flex items-center gap-2">
                        <PeriodSelector period={period} onChange={p => { setPeriod(p); setSelected(null); }} />
                        <button onClick={() => loadCampaigns(period)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                            <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>refresh</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-2xl p-1">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => { setTab(t.id); setSelected(null); }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-colors ${tab === t.id
                            ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}>
                        <span className="material-symbols-outlined text-[15px]">{t.icon}</span>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            {tab === 'campaigns' && (
                loading ? (
                    <div className="flex items-center justify-center py-20 text-slate-300">
                        <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
                    </div>
                ) : error ? (
                    <div className="rounded-2xl border border-brand-danger/20 dark:border-brand-danger/40 bg-brand-danger/8 dark:bg-brand-danger/20 p-6 text-center space-y-2">
                        <span className="material-symbols-outlined text-brand-danger text-[40px]">error</span>
                        <p className="text-sm font-semibold text-brand-danger dark:text-brand-danger">{error}</p>
                        <p className="text-xs text-brand-danger">Verifica que el token de Meta tenga permisos ads_read</p>
                    </div>
                ) : !data ? null : selected ? (
                    <AccountDetailView account={selected} onBack={() => setSelected(null)} />
                ) : (
                    <AccountListView accounts={data.adAccounts} onSelect={setSelected} />
                )
            )}
            {tab === 'conversions' && <ConversionsTab />}
            {tab === 'funnel'      && <FunnelTab />}
            {tab === 'alerts'      && <AlertsTab />}
            {tab === 'optimize'    && <OptimizeTab />}
        </div>
    );
};

export default MetaAds;
